/**
 * Бүх гутал — размераар эхэлдэг (2026-09-28).
 *
 * ⚠️ Энэ хуудас нь СИСТЕМ ДЭХ БҮХ гутал: зарагдсан, буцаагдсан,
 *    актлагдсан ч мөн адил. Байршлаар хязгаарлагдахгүй — тодорхой
 *    агуулахын дотоод зураг нь `/warehouse` дээр.
 *
 * Бүтэц: шүүлтүүр → размерын карт → сонгосон размерын гутал.
 *   · Бүх гутлыг анхнаасаа ЖАГСААХГҮЙ. Өмнө нь 1,200 гутлыг 12-оор
 *     хуудаслаж, «38 байна уу» гэхэд ажилтан хуудас хуудсаар гүйлгэдэг
 *     байв. Одоо «38 — 124 гутал» картыг дарж, зөвхөн тэр размерыг харна.
 *   · Хайсан, эсвэл шүүлтүүрт нэг ачаалалтад багтах цөөн гутал үлдсэн
 *     бол жагсаалт шууд гарна — размер сонгох шаардлагагүй.
 *   · Анхдагч төлөв «Нөөцөд» (бэлэн + буцаагдсан). Зарагдсан, актлагдсан
 *     нь төлвийн товчоор — хэд байгаа нь товч дээрээ.
 *
 * Шүүлтүүр бүр URL-д (`lib/use-items-filter.ts`) — холбоос хуваалцаж,
 * дахин ачаалж болно.
 *
 * ⚠️ Тусдаа «Скан» хуудас УСТСАН. Хайлтын талбар `autoFocus`: бүтэн
 *    серийн дугаар уншуулбал шууд тухайн гутлын хуудас руу орно.
 *
 * Баркод: прототип дээр гоёлын зураас байсныг БОДИТ Code 128-C-ээр
 * сольсон (`@boots/domain/barcode`, 7 тесттэй). Дэлгэц дээрхийг
 * уншуулж болно. Шошго хэвлэх нь ZPL → принтер — тэр нь хийгдээгүй.
 *
 * Зураг: `GET /items` нь хөрөг зургийг presigned URL-тай өгнө. Thumb нь
 * `media` боловсруулж дуустал `null` — тэр үед эх хувилбарыг харуулна.
 */

import {
    formatDate, formatLocation, formatMnt, formatMntShort, formatQty, formatSerialDisplay,
    isValidSerial, normalizeSerialInput,
} from '@boots/domain';
import type { ItemFacetsOutput, ItemSort } from '@boots/schema';
import {
  keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient,
} from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Barcode } from '../components/barcode';
import { EmptyState } from '../components/empty-state';
import { ItemFilterBar } from '../components/items/item-filter-bar';
import { SizeGrid } from '../components/items/size-grid';
import { ItemTable } from '../components/item-table';
import { Section } from '../components/section';
import { StatCard, StatGrid, StatPanel } from '../components/stat-card';
import type { SupplierRow } from '../components/supplier-form';
import {
    Button, Empty, ErrorNote, Field, Modal, Money, OkNote, Select,
    Spinner, StatusBadge, Tabs, Td, Th,
} from '../components/ui';
import { api, type Envelope, type PagedEnvelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  COST_ROLES, type ItemListRow, type Location, type StockReport, type Store,
} from '../lib/types';
import {
  CLEAR_FILTERS, activeFilterCount, itemsApiFilter, sizeFromSearch, useItemsFilter,
  type ItemsFilter, type SetItemsFilter,
} from '../lib/use-items-filter';

/** Нэг ачаалалтын гутал. Шүүлтүүрт үүнээс цөөн үлдвэл размер сонгохгүйгээр жагсаана. */
const PER_PAGE = 24;

const SORT_TABS: { key: ItemSort; label: string }[] = [
  { key: 'newest', label: 'Шинэ' },
  { key: 'oldest', label: 'Хуучин' },
  { key: 'price_asc', label: 'Хямд' },
  { key: 'price_desc', label: 'Үнэтэй' },
];

const VIEW_TABS: { key: ItemsFilter['view']; label: string }[] = [
  { key: 'grid', label: 'Сүлжээн' },
  { key: 'table', label: 'Хүснэгт' },
];

export function ItemsPage() {
  const { can, readOnly } = useAuth();
  const showCost = can(...COST_ROLES);
  // Серийн дугаар агуулахад олгогдоно (CLAUDE.md №6)
  const canLabel = can('WAREHOUSE', 'ADMIN') && !readOnly;

  const { filter, set } = useItemsFilter();
  const [bulkLabel, setBulkLabel] = useState(false);
  /** Түргэн харах — дэлгэрэнгүй хуудас руу шилжихгүйгээр. */
  const [preview, setPreview] = useState<ItemListRow | null>(null);
  const nav = useNavigate();

  /**
   * Карт эсвэл мөр дарахад ДЭЛГЭРЭНГҮЙ ХУУДАС руу. Хажуугийн панелийн
   * бүх үйлдэл (шошго, зөөх, үнэ, актлах) `/items/:id`-д аль хэдийн бий.
   */
  const openItem = (id: string) => nav(`/items/${id}`);

  const { data: stock } = useQuery({
    queryKey: ['stock-report'],
    queryFn: () => api<Envelope<StockReport>>('/reports/stock'),
    staleTime: 60_000,
  });

  // Нэгж, байршил, нийлүүлэгч — лавлах тул удаан кэшлэнэ
  const { data: locs } = useQuery({
    queryKey: ['locations'],
    queryFn: () => api<Envelope<Location[]>>('/ref/locations'),
    staleTime: 5 * 60_000,
  });
  const { data: stores } = useQuery({
    queryKey: ['ref-stores'],
    queryFn: () => api<Envelope<Store[]>>('/ref/stores'),
    staleTime: 5 * 60_000,
  });
  const { data: suppliers } = useQuery({
    queryKey: ['ref-suppliers'],
    queryFn: () => api<Envelope<SupplierRow[]>>('/ref/suppliers'),
    staleTime: 5 * 60_000,
  });

  // `['items', …]` — гутал өөрчлөгдөхөд бусад хуудас энэ түлхүүрийг
  // invalidate хийдэг тул карт дээрх тоо ч дагаж шинэчлэгдэнэ.
  const filterKey = itemsApiFilter(filter).toString();
  const facetsQuery = useQuery({
    queryKey: ['items', 'facets', filterKey],
    queryFn: () => api<Envelope<ItemFacetsOutput>>(`/items/facets?${filterKey}`),
    placeholderData: keepPreviousData,
  });
  const facets = facetsQuery.data?.data;
  const inView = facets?.sizes.reduce((n, s) => n + s.count, 0) ?? 0;
  /** Төлвийн шүүлтүүргүйгээр хэд — «Нөөцөд» хоосон үед өөр төлөвт байгаа эсэх */
  const anyStatus = facets?.statuses.reduce((n, s) => n + s.count, 0) ?? 0;

  // Жагсаалт: размер сонгосон, эсвэл хайсан / цөөн гутал үлдсэн үед бүх размер
  const listSize = filter.size || (inView > 0 && (filter.q || inView <= PER_PAGE) ? 'all' : '');

  /**
   * Хайлтын Enter.
   *  · «38» — размер (`sizeFromSearch`).
   *  · БҮТЭН серийн дугаар — шууд тэр гутал руу: скан хийсэн ажилтан нэг
   *    мөртэй жагсаалт хараад дахин дарах шаардлагагүй. Олдохгүй бол
   *    (өөр системийн шошго, буруу уншилт) энгийн хайлт болно.
   */
  async function submitSearch(raw: string) {
    const value = raw.trim();
    if (!value) { set({ q: null }); return; }

    const size = sizeFromSearch(value);
    if (size) {
      set({ size, q: null }, { push: true });
      return;
    }
    if (isValidSerial(value)) {
      try {
        const found = await api<Envelope<{ id: string }>>(`/items/by-serial/${normalizeSerialInput(value)}`);
        openItem(found.data.id);
        return;
      } catch {
        // Доош — энгийн хайлт болно
      }
    }
    set({ q: value });
  }

  const noBoots = <NoBoots filter={filter} set={set} anyStatus={anyStatus} />;

  return (
    <div className="flex flex-col gap-3">
      {/* ─────────── Статистик — товчны цаана ─────────── */}
      <StatPanel>
      <StatGrid>
        <StatCard label="Агуулахад байгаа гутал" icon="warehouse" value={formatQty(stock?.data.warehouseCount)} />
        <StatCard
          label="Шинээр орсон"
          icon="truck"
          value={formatQty(stock?.data.newThisWeek)}
          delta={{ text: 'Энэ 7 хоногт', direction: 'up', good: true }}
        />
        <StatCard
          label="Салбарт шилжсэн"
          icon="store"
          value={formatQty(stock?.data.movedToRetail)}
          delta={{ text: 'Энэ сард' }}
        />
        {showCost ? (
          <StatCard
            label="Агуулахын өртөг"
            icon="coins"
            value={formatMntShort(stock?.data.value?.landedCostMnt)}
            delta={{ text: `Дундаж ${formatMnt(stock?.data.value?.avgLandedMnt)}` }}
          />
        ) : (
          <StatCard label="Шүүлтэд тохирсон" icon="box" value={formatQty(inView)} delta={{ text: 'ширхэг' }} />
        )}
      </StatGrid>
      </StatPanel>

      <ItemFilterBar
        filter={filter}
        set={set}
        facets={facets}
        stores={stores?.data ?? []}
        locations={locs?.data ?? []}
        suppliers={suppliers?.data ?? []}
        onSearch={(v) => void submitSearch(v)}
        actions={canLabel ? (
          <button
            type="button"
            onClick={() => setBulkLabel(true)}
            className="inline-flex min-h-11 items-center rounded-full bg-accent px-[11px]
              py-1 text-xs whitespace-nowrap text-white transition hover:bg-accent-fill-hover
              active:translate-y-[0.5px] sm:min-h-0"
          >
            Бөөнөөр шошгожуулах
          </button>
        ) : null}
      />

      {/* ─────────── Размер — хуудасны нүүр ─────────── */}
      <Section
        title="Размер"
        subtitle={facets ? `${formatQty(inView)} гутал · ${formatQty(facets.sizes.length)} размер` : undefined}
        note={facetsQuery.isFetching ? 'шинэчилж байна…' : listSize ? undefined : 'размер дээр дарж гутлыг нь харна'}
      >
        {facetsQuery.error ? (
          <ErrorNote>{(facetsQuery.error as Error).message}</ErrorNote>
        ) : !facets ? (
          <Spinner />
        ) : inView === 0 && !filter.size ? (
          noBoots
        ) : (
          <SizeGrid
            sizes={facets.sizes}
            value={listSize}
            showSplit={!filter.storeId}
            // Сонгосон картыг дахин дарвал размерын нүүр рүү буцна
            onPick={(k) => set({ size: k === filter.size ? null : k }, { push: true })}
          />
        )}
      </Section>

      {listSize ? (
        <ItemResults
          filter={{ ...filter, size: listSize }}
          set={set}
          showCost={showCost}
          empty={noBoots}
          onOpen={openItem}
          onPreview={setPreview}
        />
      ) : null}

      {preview ? (
        <ItemPreview
          item={preview}
          onClose={() => setPreview(null)}
          onOpen={() => {
            const id = preview.id;
            setPreview(null);
            openItem(id);
          }}
        />
      ) : null}

      {bulkLabel ? <BulkLabelModal onClose={() => setBulkLabel(false)} /> : null}
    </div>
  );
}

/**
 * Сонгосон размерын (эсвэл хайлтын) гутал.
 *
 * Хуудаслахгүй — «Цааш үзэх» доош нь залгана: 124 гутлыг үзэж байхад
 * өмнө харсан нь дэлгэцээс алга болохгүй.
 */
function ItemResults({ filter, set, showCost, empty, onOpen, onPreview }: {
  filter: ItemsFilter;
  set: SetItemsFilter;
  showCost: boolean;
  empty: ReactNode;
  onOpen: (id: string) => void;
  onPreview: (it: ItemListRow) => void;
}) {
  const params = itemsApiFilter(filter);
  if (filter.sort !== 'newest') params.set('sort', filter.sort);
  params.set('limit', String(PER_PAGE));
  const key = params.toString();

  const list = useInfiniteQuery({
    queryKey: ['items', 'browse', key],
    queryFn: ({ pageParam }) =>
      api<PagedEnvelope<ItemListRow>>(`/items?${key}${pageParam ? `&cursor=${pageParam}` : ''}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.meta.nextCursor,
    placeholderData: keepPreviousData,
  });

  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const total = list.data?.pages[0]?.meta.total ?? 0;

  return (
    <Section
      title={resultTitle(filter)}
      subtitle={list.data ? `${formatQty(total)} гутал` : undefined}
      // Размер солиход өмнөх размерын гутал шинэ нь ирэх хүртэл харагдана
      note={
        list.isPlaceholderData ? 'шинэчилж байна…'
          : rows.length < total ? `${formatQty(rows.length)} нь харагдаж байна` : undefined
      }
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Tabs tabs={SORT_TABS} value={filter.sort} onChange={(v) => set({ sort: v === 'newest' ? null : v })} />
          <Tabs tabs={VIEW_TABS} value={filter.view} onChange={(v) => set({ view: v === 'grid' ? null : v })} />
        </div>
      }
    >
      {list.error ? (
        <ErrorNote>{(list.error as Error).message}</ErrorNote>
      ) : list.isPending ? (
        <Spinner />
      ) : rows.length === 0 ? (
        empty
      ) : filter.view === 'grid' ? (
        <ItemGrid rows={rows} onPick={onOpen} onPreview={onPreview} />
      ) : (
        <ItemTable rows={rows} onPick={onOpen} showCost={showCost} />
      )}

      {list.hasNextPage ? (
        <Button variant="ghost" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
          {list.isFetchingNextPage ? 'Ачаалж байна…' : `Цааш үзэх · ${formatQty(total - rows.length)} үлдсэн`}
        </Button>
      ) : null}
    </Section>
  );
}

function resultTitle(f: ItemsFilter): string {
  if (f.size === 'none') return 'Размергүй гутал';
  if (f.size !== 'all') return `${f.size} размер`;
  return f.q ? `«${f.q}» хайлт` : 'Бүх размер';
}

/**
 * Хоосон үр дүн — ЯАГААД, дараа нь ЮУ ХИЙХ (`EmptyState`-ийн зарчим).
 * «Нөөцөд» алга ч зарагдсан, актлагдсан дунд байвал тэр рүү нэг товч.
 */
function NoBoots({ filter, set, anyStatus }: {
  filter: ItemsFilter;
  set: SetItemsFilter;
  anyStatus: number;
}) {
  const elsewhere = filter.status === '' && anyStatus > 0;
  const filtered = activeFilterCount(filter) > 0;

  return (
    <EmptyState
      compact
      title={filter.q ? `«${filter.q}» олдсонгүй` : 'Энэ шүүлтүүрт гутал алга'}
      description={
        elsewhere
          ? 'Нөөцөд алга — зарагдсан, актлагдсан гутлын дунд байж болно.'
          : filtered ? 'Шүүлтүүрээ сулруулж үзнэ үү.' : undefined
      }
      action={
        elsewhere ? (
          <Button variant="ghost" onClick={() => set({ status: 'all' })}>
            Бүх төлөвөөс харах ({formatQty(anyStatus)})
          </Button>
        ) : filtered ? (
          <Button variant="ghost" onClick={() => set(CLEAR_FILTERS)}>Шүүлтүүр цэвэрлэх</Button>
        ) : undefined
      }
    />
  );
}

// ============================================================
//  Бөөнөөр шошгожуулах — агуулахын гол урсгал
// ============================================================
function BulkLabelModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [locationId, setLocationId] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [assigned, setAssigned] = useState<{ id: string; serial: string }[] | null>(null);
  const [zpl, setZpl] = useState<string | null>(null);

  const locs = useQuery({
    queryKey: ['ref-locations'],
    queryFn: () => api<Envelope<Location[]>>('/ref/locations?active=true'),
  });

  const list = useQuery({
    queryKey: ['items-unlabeled', locationId],
    queryFn: () =>
      api<{ data: ItemListRow[]; meta: { total: number } }>(
        `/items?unlabeled=true&status=COLLECTED&limit=100${locationId ? `&locationId=${locationId}` : ''}`,
      ),
  });

  const assign = useMutation({
    mutationFn: () =>
      api<{ assigned: number; skipped: number; items: { id: string; serial: string }[] }>(
        '/items/bulk-assign-serial',
        { method: 'POST', body: { itemIds: [...selected] } },
      ),
    onSuccess: async (res) => {
      setAssigned(res.items);
      setSelected(new Set());
      await qc.invalidateQueries({ queryKey: ['items'] });
      await qc.invalidateQueries({ queryKey: ['items-unlabeled'] });
      await qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const print = useMutation({
    mutationFn: () =>
      api<Envelope<{ zpl: string; count: number }>>('/items/bulk-print-labels', {
        method: 'POST',
        body: { itemIds: (assigned ?? []).map((a) => a.id) },
      }),
    onSuccess: (res) => setZpl(res.data.zpl),
  });

  const rows = list.data?.data ?? [];
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));

  return (
    <Modal
      wide
      title="Бөөнөөр серийн дугаар олгох"
      subtitle="Серийн дугаар зөвхөн агуулахад олгогдоно, Оросод биш"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Хаах</Button>
          {assigned ? (
            zpl ? (
              <Button onClick={() => void navigator.clipboard.writeText(zpl)}>ZPL хуулах</Button>
            ) : (
              <Button onClick={() => print.mutate()} disabled={print.isPending}>
                {print.isPending ? 'Үүсгэж байна…' : `Шошго үүсгэх (${formatQty(assigned.length)})`}
              </Button>
            )
          ) : (
            <Button onClick={() => assign.mutate()} disabled={selected.size === 0 || assign.isPending}>
              {assign.isPending ? 'Олгож байна…' : `Серийн олгох (${formatQty(selected.size)})`}
            </Button>
          )}
        </>
      }
    >
      {assigned ? (
        <>
          <OkNote>{formatQty(assigned.length)} гуталд серийн дугаар олгогдлоо</OkNote>
          <div className="table-pin max-h-[34dvh] overflow-y-auto rounded-card border border-hair">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 bg-well-2">
                <tr><Th>Серийн дугаар</Th></tr>
              </thead>
              <tbody>
                {assigned.map((a) => (
                  <tr key={a.id}><Td><span className="num">{formatSerialDisplay(a.serial)}</span></Td></tr>
                ))}
              </tbody>
            </table>
          </div>
          {zpl ? (
            <>
              <p className="text-[12.5px] text-ink-2">
                Хуулаад принтер рүү илгээнэ үү — хөтчөөс TCP :9100 руу шууд холбогдох боломжгүй.
              </p>
              <pre className="max-h-48 overflow-auto rounded-card border border-hair bg-well p-3 font-mono text-[11px] whitespace-pre-wrap">
                {zpl}
              </pre>
            </>
          ) : null}
          {print.error ? <ErrorNote>{(print.error as Error).message}</ErrorNote> : null}
        </>
      ) : (
        <>
          <Field label="Байршил">
            <Select
              value={locationId}
              onChange={(v) => { setLocationId(v); setSelected(new Set()); }}
              placeholder="Бүх байршил"
              options={(locs.data?.data ?? []).map((l) => ({ value: l.id, label: `${l.code} · ${l.name}` }))}
            />
          </Field>

          {list.isPending ? <Spinner /> : rows.length === 0 ? (
            <Empty>Шошгогүй бараа алга</Empty>
          ) : (
            <>
              <label className="flex items-center gap-2 text-[13px] text-ink-2">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())}
                  className="size-4 accent-accent"
                />
                Бүгдийг сонгох ({formatQty(rows.length)})
              </label>
              <div className="table-pin max-h-[42dvh] overflow-y-auto rounded-card border border-hair">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 bg-well-2">
                    <tr><Th> </Th><Th>Загвар</Th><Th num>Размер</Th><Th>Байршил</Th></tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={r.id}
                        onClick={() => setSelected((prev) => {
                          const next = new Set(prev);
                          if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
                          return next;
                        })}
                        className="cursor-pointer transition hover:bg-row-hover"
                      >
                        <Td>
                          <input type="checkbox" readOnly checked={selected.has(r.id)} className="size-4 accent-accent" />
                        </Td>
                        <Td>{r.model.brand} · {r.model.name}</Td>
                        <Td num>{r.size}</Td>
                        <Td className="text-ink-2">{formatLocation(r.location)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {assign.error ? <ErrorNote>{(assign.error as Error).message}</ErrorNote> : null}
        </>
      )}
    </Modal>
  );
}

// ============================================================
//  Хэсгүүд
// ============================================================

/**
 * Гутлын карт — баркод, доор нь ЗУРАГ.
 *
 * ⚠️ Зураг нь жагсаалтын хариунд аль хэдийн ирдэг (`photo`). Өмнө нь
 *    карт бүр `/items/:id/photos`-ыг тусад нь дууддаг байсан: нэг
 *    хуудсанд 12 нэмэлт хүсэлт, тус бүр өөрийн presign-тэй.
 *
 * ⚠️ Зургийг ХАРАХЫН тулд гутал бүр дээр дарах шаардлагагүй болов
 *    (2026-09-25): баркодны доор жижгээр харагдана, томруулж харах
 *    товч нь дэлгэрэнгүй хуудас руу ОРУУЛАХГҮЙ.
 */
function ItemGrid({
  rows, onPick, onPreview,
}: {
  rows: ItemListRow[];
  onPick: (id: string) => void;
  onPreview: (it: ItemListRow) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 2xl:grid-cols-4">
      {rows.map((it) => {
        return (
          <div
            key={it.id}
            className="group relative flex flex-col overflow-hidden rounded-well border
              border-hair bg-well-3 text-left transition hover:border-accent-300 hover:shadow-panel"
          >
            <button
              type="button"
              onClick={() => onPick(it.id)}
              className="flex flex-col text-left"
            >
              {/* Шошготой → баркод (дэлгэцээс шууд уншуулна) */}
              <div className="relative flex h-[116px] items-center justify-center overflow-hidden bg-white px-4 pt-7">
                {it.serial ? (
                  <Barcode serial={it.serial} height={44} className="w-full text-ink" />
                ) : (
                  <span className="text-[12.5px] text-ink-3">Шошгогүй</span>
                )}
                <span className="absolute top-2 left-2">
                  <StatusBadge status={it.status} />
                </span>
              </div>

              <div className="flex flex-col gap-1.5 px-3 pt-2.5 pb-3">
                <div className="flex items-baseline gap-2">
                  <span className="num text-[14.5px] font-semibold">
                    <Money value={it.priceMnt} />
                  </span>
                  <span className="num ml-auto text-[12.5px] whitespace-nowrap text-ink-2">
                    {it.size}
                  </span>
                </div>
                {/* Загварын нэр БИШ — шинэ гутал бүгд «Ерөнхий» загварт
                    ордог тул юу ч хэлдэггүй (CLAUDE.md: загварын нэр UI-д
                    гаргахгүй). Нийлүүлэгч нь шүүлтүүртэй ижил үг. */}
                <span className="text-[12.5px] text-pretty">{it.supplier.name}</span>
                <span className="text-[11.5px] text-ink-3 text-pretty">
                  {formatLocation(it.location)}
                </span>
              </div>
            </button>

            {/*
              Түргэн харах — дэлгэрэнгүй хуудас руу ОРОХГҮЙГЭЭР зураг,
              гол мэдээллийг томоор. Хулгана картан дээр ирэхэд гарна;
              гар, хүрэлтээр (`focus`) ч хүрнэ.
            */}
            <button
              type="button"
              onClick={() => onPreview(it)}
              aria-label={`${it.serial ? formatSerialDisplay(it.serial) : 'Шошгогүй гутал'} — түргэн харах`}
              className="absolute top-2 right-2 grid size-8 place-items-center rounded-full
                border border-hair bg-panel/90 text-ink-2 opacity-0 transition
                group-hover:opacity-100 hover:text-ink focus-visible:opacity-100"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden>
                <g stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                  <path d="M1.6 8s2.4-4.2 6.4-4.2S14.4 8 14.4 8s-2.4 4.2-6.4 4.2S1.6 8 1.6 8Z" />
                  <circle cx="8" cy="8" r="1.8" />
                </g>
              </svg>
            </button>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Түргэн харах — зураг том, гол мэдээлэл, дэлгэрэнгүй рүү холбоос.
 *
 * ⚠️ Дэлгэрэнгүй хуудсыг ОРЛОХГҮЙ: түүх, үйлдэл, зургийн самбар
 *    тэнд хэвээр. Энэ нь «энэ аль гутал байсан бэ» гэдгийг хариулна.
 */
function ItemPreview({ item, onClose, onOpen }: {
  item: ItemListRow;
  onClose: () => void;
  onOpen: () => void;
}) {
  const url = item.photo?.mediumUrl ?? item.photo?.thumbUrl ?? item.photo?.originalUrl ?? null;

  return (
    <Modal
      title={item.serial ? formatSerialDisplay(item.serial) : 'Шошгогүй гутал'}
      subtitle={item.model.name}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Хаах</Button>
          <Button onClick={onOpen}>Дэлгэрэнгүй</Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/*
          ⚠️ ХАРЬЦААГ ТУЛГАХГҮЙ. Өмнө нь 4:3 хайрцагт суулгадаг байсан:
             утсаар авсан БОСОО зураг хоёр талдаа хоосон зайтай, жинхэнэ
             хэлбэрээсээ өөр харагддаг байв (2026-09-25). Одоо зураг
             өөрийн харьцаагаараа — зөвхөн өндөр нь хязгаарлагдана.
        */}
        <div className="grid place-items-center overflow-hidden rounded-card border border-hair bg-well">
          {url ? (
            <img src={url} alt="" className="max-h-[58vh] w-auto max-w-full object-contain" />
          ) : (
            <span className="py-10 text-[12.5px] text-ink-3">Зураг байхгүй</span>
          )}
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
          <Fact k="Төлөв"><StatusBadge status={item.status} /></Fact>
          <Fact k="Размер">{item.size ?? '—'}</Fact>
          <Fact k="Үнэ"><Money value={item.priceMnt} /></Fact>
          <Fact k="Байршил">{formatLocation(item.location)}</Fact>
          <Fact k="Нийлүүлэгч">{item.supplier?.name ?? '—'}</Fact>
          <Fact k="Бүртгэсэн">{formatDate(item.collectedAt)}</Fact>
        </dl>
      </div>
    </Modal>
  );
}

function Fact({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[11.5px] text-ink-3">{k}</dt>
      <dd className="text-pretty">{children}</dd>
    </div>
  );
}
